import { ChatEnum, ChatState } from 'src/components/Chat/Chat.enum.ts';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context.ts';
import { useUserContext } from 'src/providers/UserProvider/User.context.ts';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context.ts';
import { useMessageBarContext } from 'src/components/Chat/components/MessageBar/MessageBar.hook.ts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook.ts';
import { tGenericKeys } from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.types.ts';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum.ts';
import {
  tWapiButtonRequest,
  tWapiSendTemplateRequest,
  tWapiTemplateParameter,
} from 'src/components/Chat/Chat.types.ts';
import { ChatService } from 'src/components/Chat/Chat.service.ts';
import { FileUploadService } from 'src/services/FileUpload/FileUpload.service.ts';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { resolveSendErrorAlert } from 'src/utils/sendErrorMessage.ts';
import { useBulkMessage } from 'src/pages/Pipe/modals/BulkMessageWABModal/BulkMessage.hook.ts';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context.ts';
import { useChatContext } from 'src/components/Chat/Chat.hook.ts';
import { withHumanAction } from 'src/components/Chat/Chat.humanAction';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { tSentTemplateCounter } from 'src/pages/WhatsappApi/components/TemplatesTable/SentTemplateCounter.types.ts';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { tPredefinedVariableDefinition } from 'src/pages/Pipe/Pipe.types.ts';
import { buildTemplateMedia } from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.functions.ts';
import {
  isAllowedTemplateMediaFile,
  templateFileTypeFromUrl,
  templateHeaderMediaKind,
  templateHeaderRequiresMedia,
} from 'src/utils/templateMedia.ts';
import {
  getMaxFileSizeForChannel,
  getWapiTemplateMediaLimits,
} from 'src/components/Chat/components/MessageBar/components/ToolActions/components/DriveFilesAction/components/UploadFileAction/UploadFileAction.constants';
import { PhotoIcon } from '@heroicons/react/16/solid';
import {
  useWalletSendGate,
  isTemplateFreeAtSource,
} from 'src/pages/Wallet/hooks/useWalletSendGate';
import { useACL } from 'src/providers/ACLProvider/ACL.hook';

export default function useWabTemplatesVariablesAction() {
  // RET-2103: this hook translates entries out of BOTH `chat/chat` (its own
  // generic send-failure copy, kept first/default so it still resolves
  // without an explicit `ns`) and `errors/errors` (the wire-code directory
  // above). Declaring only 'chat/chat' worked by accident whenever some
  // other mounted provider happened to have already loaded `errors/errors`
  // — the exact namespace-not-loaded bug this ticket fixes elsewhere.
  const { t } = useTranslation(['chat/chat', 'errors/errors']);
  // Sólo Owner/Admin recargan (DS-03 §3, mismo criterio que
  // Template.component.tsx). El provider no se toca, sólo se consume.
  const { userIsOwner, userIsAdmin } = useACL();
  const canTopUpBalance = userIsOwner || userIsAdmin;

  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUserId } = useUserContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { wapiVariables, setSelectedAction } = useMessageBarContext();
  const [loading, setLoading] = useState<boolean>(false);
  const { addAlert } = useToastContext();
  const [paramValues, setParamValues] = useState<tGenericKeys>({});
  const [imageUrl, setImageUrl] = useState<string>('');
  // Original filename of an uploaded document header, kept in lockstep with
  // imageUrl so it is sent to WBS as media.filename (BUG-475).
  const [imageFileName, setImageFileName] = useState<string>('');
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  const [blockImgText, setBlockImgText] = useState<boolean>(false);
  const imageRef = useRef<HTMLInputElement | null>(null);
  const { getMediaTypeFromUrl } = useBulkMessage();
  // Media preview load failures (CSP-blocked or expired Meta example URLs,
  // bad pasted URLs) swap the broken <img>/<video> for an explanatory
  // placeholder (BUG-567).
  const [previewFailed, setPreviewFailed] = useState<boolean>(false);
  const { setTemplateCounter } = useWhatsappApiContext();
  const { selectedChat, setSelectedChat } = useChatContext();
  const { trackWapiEvent } = useWapiAnalytics();
  const { blockWapiPlanSend } = useWapiPlanSendGuard();
  const [isInactiveModalOpen, setIsInactiveModalOpen] =
    useState<boolean>(false);
  const [isBlockedLeadModalOpen, setIsBlockedLeadModalOpen] =
    useState<boolean>(false);
  const [pendingRequest, setPendingRequest] =
    useState<tWapiSendTemplateRequest | null>(null);
  const [couponValues, setCouponValues] = useState<Record<number, string>>({});
  // RET-1806, DS-03 §5: el aviso + bloqueo de saldo insuficiente. Este camino
  // NO tiene un embudo único (B.1 handleSendTemplate y B.2 confirmInactiveModal
  // no comparten función), así que el gate se llama en LOS DOS por separado,
  // pero comparten el mismo estado (isBlocked/costLabel/shortfall) para
  // que la barra sea una sola independientemente de por dónde se disparó.
  const { isBlocked, costLabel, shortfall, checkBalance } = useWalletSendGate(
    selectedChat?.id,
  );
  /**
   * Corta el segundo clic en la ventana donde `loading` todavía es `false`.
   *
   * `setLoading(true)` corre al entrar a `handleSendTemplate`, pero `loading` es
   * estado de React: dos clics en el MISMO tick lo leen en `false` los dos y
   * ambos siguen — y ahora, entre medias, hay un `await checkBalance(...)` que
   * ensancha la ventana. En `confirmInactiveModal` el `await` va directamente
   * antes del `setLoading(true)`, así que ahí no hay guardia de ningún tipo.
   *
   * Un ref se actualiza de forma SINCRÓNICA, que es lo único que sirve contra un
   * doble clic. Es el mismo patrón que ya usa el camino sin variables
   * (`isSendingRef` en Template.component.tsx); aquí faltaba en las dos
   * entradas. Se suelta en el `finally` de cada una, junto con `loading`.
   */
  const isSendingRef = useRef(false);
  // UC-09 / RET-1806 fix del bloqueante #1 (ronda 1): la misma verdad de
  // "gratis en origen" que Template.component.tsx pinta en su badge,
  // calculada UNA vez por render y reusada en las DOS entradas de este
  // camino (handleSendTemplate y confirmInactiveModal) y en el tracking.
  const isFreeAtSource = isTemplateFreeAtSource({
    templateType: wapiVariables?.template_type ?? '',
    isChatActive: selectedChat?.state === ChatState.ACTIVE,
    hasFreeEntryPoint: !!selectedChat?.freeEntryPointData?.hasFreeEntryPoint,
  });

  const handleCouponChange = (index: number, value: string) => {
    setCouponValues((prev) => ({ ...prev, [index]: value }));
  };

  const predefinedKeys = useMemo(() => {
    if (!wapiVariables?.parameters) return new Set<string>();

    const varDefs = wapiVariables.variableDefinitions;
    const keys = new Set<string>();

    const defsMap = new Map<string, tPredefinedVariableDefinition>();
    varDefs?.forEach((def) => defsMap.set(def.variable_name, def));

    wapiVariables.parameters.forEach((param) => {
      if (defsMap.has(param.name)) {
        keys.add(param.name);
      }
    });

    return keys;
  }, [wapiVariables?.parameters, wapiVariables?.variableDefinitions]);

  const leadContextVars = useMemo<Record<string, string>>(
    () => ({
      nombre_del_lead: selectedLeadForChatModal?.name || '',
      correo_electronico: selectedLeadForChatModal?.email || '',
      correo_electrónico: selectedLeadForChatModal?.email || '',
      empresa: selectedLeadForChatModal?.company || '',
      medio: selectedLeadForChatModal?.chat?.integration || '',
    }),
    [selectedLeadForChatModal],
  );

  useEffect(() => {
    if (!wapiVariables?.parameters) return;

    const apiVars = wapiVariables.predefinedVariables;
    const varDefs = wapiVariables.variableDefinitions;
    const prefilledValues: tGenericKeys = {};

    const defsMap = new Map<string, tPredefinedVariableDefinition>();
    varDefs?.forEach((def) => defsMap.set(def.variable_name, def));

    wapiVariables.parameters.forEach((param) => {
      const name = param.name;
      const def = defsMap.get(name);
      if (def) {
        const apiValue = apiVars?.[name];
        const leadValue = leadContextVars[name];
        prefilledValues[name] =
          apiValue ?? (leadValue || undefined) ?? def.example;
      }
    });

    setParamValues((prev) => ({ ...prev, ...prefilledValues }));
  }, [
    wapiVariables?.parameters,
    wapiVariables?.predefinedVariables,
    wapiVariables?.variableDefinitions,
    leadContextVars,
  ]);

  const handleChange = (name: string, value: string) => {
    setParamValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * ¿El header de esta plantilla exige un archivo adjunto?
   *
   * Se calcula UNA vez y lo consumen los SIETE sitios que antes duplicaban
   * `wapiVariables?.header?.startsWith('https://')` — seis aquí y el bloque
   * de adjuntar entero en el componente. Esa duplicación es exactamente la
   * razón de que el bug tuviera siete cabezas: cuando el ejemplo del header
   * es un handle de upload opaco (`4:MDFN…:aW1hZ2UvanBlZw==:ARbV…`, lo que
   * Meta devuelve para plantillas creadas en Business Manager), el predicado
   * de la URL respondía "no lleva media" y el modal se pintaba SIN
   * adjuntador, mandando el envío sin `media` — Meta lo tumba con 132012 /
   * `wba-10038` (BUG-866, KTD7).
   *
   * El `||` con el predicado de la URL se conserva (KTD8): sólo puede
   * ampliar, nunca restringir, así que las filas legacy sin `format` siguen
   * encendiendo el uploader por donde lo encendían.
   */
  const headerNeedsMedia =
    templateHeaderRequiresMedia(wapiVariables?.format) ||
    !!wapiVariables?.header?.startsWith('https://');

  /**
   * Media kind the template's header expects. Prefers the header's declared
   * `format`; falls back to inferring it from the example URL, which is all
   * the legacy generic `MEDIA` format and format-less rows can offer.
   * Meta-synced templates often only carry a scontent.whatsapp.net
   * header_handle, and when no extension is recognizable this lands on
   * 'file' and any supported media is accepted (BUG-567, BUG-866).
   */
  const expectedMediaKind = useMemo(
    () => templateHeaderMediaKind(wapiVariables?.format, wapiVariables?.header),
    [wapiVariables?.format, wapiVariables?.header],
  );

  /** Size limits interpolated into the header media guidance (BUG-1039). */
  const mediaLimits = useMemo(
    () => getWapiTemplateMediaLimits(expectedMediaKind),
    [expectedMediaKind],
  );

  /**
   * Validates if the URL has a valid file extension matching the expected kind
   * @param url - The URL to validate
   * @returns true if the URL has a valid extension matching the expected kind, false otherwise
   */
  const isValidUrlFormat = (url: string): boolean => {
    if (!url || url.trim() === '') return false; // Require URL

    // Same extension rule as picked files, applied to the query-stripped URL.
    return isAllowedTemplateMediaFile(url.split('?')[0], expectedMediaKind);
  };

  /**
   * Handles the URL input change. Format feedback is rendered inline by the
   * component (a per-keystroke error toast fired while the user was still
   * typing the URL — BUG-567).
   */
  const handleUrlChange = (value: string) => {
    setImageUrl(value);
    // A pasted/typed URL has no associated file; drop any filename captured from
    // a prior upload so it cannot ride along on the next send (BUG-475).
    setImageFileName('');
  };

  async function uploadImage(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files.length === 0 || !currentWorkspaceId)
      return;
    const file = e.target.files[0];
    // Template sends always go through the WhatsApp Cloud API: per-type limits
    // (image 5MB, video 16MB, document 100MB) instead of a flat 5MB cap that
    // rejected valid videos/PDFs before the upload ever ran (BUG-714).
    const { maxSize, humanLimit } = getMaxFileSizeForChannel(
      'WHATSAPP_API',
      file,
    );
    if (file.size > maxSize) {
      addAlert({
        type: eAlertType.ERROR,
        message: t('page.errors.file-too-large-limit', { limit: humanLimit }),
      });
      return;
    }

    // Always validate the pick — the input's `accept` is wider than the
    // pipeline supports so HEIC/WebP/MOV stay pickable and get this
    // explanatory error instead of a silently greyed-out dialog (BUG-567).
    if (!isAllowedTemplateMediaFile(file.name, expectedMediaKind)) {
      addAlert({
        type: eAlertType.ERROR,
        message: t(
          `wabTemplatesVariablesAction.invalidFileType.${expectedMediaKind}`,
        ),
      });
      return;
    }

    setLoadingImage(true);
    try {
      const fileUrl = await FileUploadService.uploadFileToS3({
        file,
        workspaceId: currentWorkspaceId,
        validateMimeType: true,
      });
      setImageUrl(fileUrl || '');
      setImageFileName(file.name);
      setBlockImgText(true);
    } catch (e) {
      addAlert({
        type: eAlertType.ERROR,
        message: `Error uploading image: ${e}`,
      });
    }
    setLoadingImage(false);
  }

  /**
   * Generates a preview of the template by replacing the variables in the template text
   * with the values provided in `paramValues`.
   *
   * @returns {string | undefined} The template with variables replaced by their values, or `undefined` if there is no template.
   */
  const renderTemplate = () => {
    let text = wapiVariables?.template;
    Object.entries(paramValues).forEach(([key, value]) => {
      text = text?.replace(
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
        value || `{{${key}}}`,
      );
    });
    return text;
  };

  const renderHeader = () => {
    let header = wapiVariables?.header;
    Object.entries(paramValues).forEach(([key, value]) => {
      header = header?.replace(
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
        value || `{{${key}}}`,
      );
    });
    if (headerNeedsMedia) {
      const mediaUrl =
        imageUrl !== '' ? imageUrl : (wapiVariables?.header ?? '');
      const fileType = templateFileTypeFromUrl(mediaUrl);
      // BUG-866: con un handle de upload opaco por ejemplo, `mediaUrl` no es
      // una URL. Meterlo en `<img src>` pinta el glifo de roto del navegador
      // y espera un `onError` para recuperarse. No hay nada que
      // previsualizar todavía — lo habrá cuando el usuario adjunte su
      // archivo — así que se dice, en vez de fingir un preview.
      const hasPreviewableUrl = mediaUrl.startsWith('https://');

      // Meta example URLs (scontent.whatsapp.net) can be CSP-blocked or
      // expired, and pasted URLs can simply be wrong — show an explanatory
      // placeholder instead of the browser's broken-media glyph (BUG-567).
      if (previewFailed || !hasPreviewableUrl) {
        return (
          <div className="flex justify-center items-center flex-col">
            <PhotoIcon className="w-12 h-12 text-gray-400" />
            <span className="text-xs text-gray-500 mt-2">
              {t('wabTemplatesVariablesAction.previewUnavailable')}
            </span>
          </div>
        );
      }

      if (fileType === 'pdf') {
        return (
          <div className="flex justify-center items-center flex-col">
            <svg
              className="w-20 h-20 text-gray-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18.5,9H13V3.5L18.5,9M6,20V4H12V10H18V20H6Z" />
            </svg>
            <span className="text-xs text-gray-500 mt-2">Documento PDF</span>
          </div>
        );
      }

      if (fileType === 'mp4') {
        return (
          <div className="flex justify-center">
            <video
              src={mediaUrl}
              controls
              className="max-w-40 rounded-md"
              onError={() => setPreviewFailed(true)}
            />
          </div>
        );
      }
      return (
        <div className="flex justify-center">
          <img
            src={mediaUrl}
            alt="header"
            className="max-w-40 rounded-md"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      );
    }
    return header;
  };

  const handleSendTemplate = async () => {
    if (
      !currentWorkspaceId ||
      !selectedLeadForChatModal?.handle ||
      loading ||
      isSendingRef.current
    )
      return;
    // RET-2349 (R5): a template always goes out by WhatsApp API. The plan
    // refuses it before the balance check is consulted, not instead of it.
    if (blockWapiPlanSend(eChannelType.WHATSAPP_API)) return;
    isSendingRef.current = true;
    setLoading(true);

    const parameters: tWapiTemplateParameter[] = [];
    const mediaType = getMediaTypeFromUrl(imageUrl);

    if (headerNeedsMedia) {
      parameters.push({
        name: 'MEDIA',
        value: imageUrl,
        type: 'header',
        format: mediaType,
      });
    }

    if (wapiVariables?.parameters) {
      wapiVariables.parameters.forEach((param) => {
        const name = param?.name;
        const value = paramValues[name];
        let type: 'body' | 'header' = 'body';
        const templateHas = wapiVariables?.template?.includes(`{{${name}}}`);
        const headerHas = wapiVariables?.header?.includes(`{{${name}}}`);
        if (headerHas) {
          type = 'header';
        } else if (templateHas) {
          type = 'body';
        }
        if (!(headerNeedsMedia && type === 'header')) {
          parameters.push({
            name,
            value,
            type,
            format: 'text',
          });
        }
      });
    }

    const buttons: tWapiButtonRequest[] = (
      wapiVariables?.copyCodeButtons ?? []
    ).map((btn) => ({
      type: 'button',
      sub_type: 'COPY_CODE',
      payload: couponValues[btn.index] ?? '',
      index: btn.index,
    }));

    const request: tWapiSendTemplateRequest = {
      to: selectedLeadForChatModal.handle,
      template_id: wapiVariables?.template_id || '',
      parameters,
      ...(buttons.length > 0 && { buttons }),
      // Attribute the send to the current user so WBS persists it and the
      // template bubble shows the sender's name (INT-922).
      ...(currentUserId && { created_by: currentUserId }),
    };
    if (headerNeedsMedia) {
      // Set media (with filename for documents) before the pendingRequest
      // snapshot below so the inactive-chat resend path inherits it (BUG-475).
      request.media = buildTemplateMedia({
        mediaType,
        url: imageUrl,
        fileName: imageFileName,
      });
    }

    if (!selectedChat?.undeliveredMessage?.canSendMessages) {
      setIsBlockedLeadModalOpen(true);
      isSendingRef.current = false;
      setLoading(false);
      return;
    }

    // Count templates according to chat state
    const isInactive = selectedChat?.state === ChatState.INACTIVE;
    const counter = selectedChat?.sentTemplateCounter;
    const totalTemplatesWhileClosed =
      (counter?.marketingWhileClosed || 0) + (counter?.utilityWhileClosed || 0);

    // If user has sent 1 template or more, show inactive modal
    if (isInactive && totalTemplatesWhileClosed > 0) {
      setPendingRequest(request);
      setIsInactiveModalOpen(true);
      // El envio sigue vivo en confirmInactiveModal, que vuelve a tomar el ref.
      isSendingRef.current = false;
      setLoading(false);
      return;
    }

    // RET-1806, DS-03: corre DESPUÉS de los gates de hoy (lead bloqueado /
    // conversación inactiva ya decidieron no llegar aquí) — a un lead que no
    // puede recibir mensajes no se le vende una recarga que no va a poder
    // usar.
    const canSend = await checkBalance({
      templateType: wapiVariables?.template_type ?? '',
      recipient: selectedLeadForChatModal.handle,
      isFreeAtSource,
    });
    if (!canSend) {
      // Se suelta SIEMPRE: un veredicto bloqueado no puede trancar el boton, o
      // seria el mismo callejon que el `isBlocked ||` de Template.component.
      isSendingRef.current = false;
      setLoading(false);
      return;
    }

    try {
      // INT-1538 U10: a person sending from the chat; recorded first.
      await withHumanAction(currentWorkspaceId, selectedChat, () =>
        ChatService.sendWapiFollowTemplate(currentWorkspaceId, request),
      );

      const isFreeMessage = isFreeAtSource;
      const isMarketing =
        wapiVariables?.template_type === 'MARKETING' ||
        wapiVariables?.template_type === 'FOLLOWING';
      const templateName = wapiVariables?.title ?? '';
      const templateId = wapiVariables?.template_id ?? '';

      if (isFreeMessage) {
        trackWapiEvent(
          isMarketing
            ? WAPI_EVENTS.MESSAGE_TEMPLATE_MARKETING_FREE_SENT
            : WAPI_EVENTS.MESSAGE_TEMPLATE_UTILITY_FREE_SENT,
          {
            templateId,
            templateName,
            recipientNumber: selectedLeadForChatModal.handle,
            conversationId: selectedChat?.id ?? '',
            isFreeMessage: true,
          },
        );
      } else {
        trackWapiEvent(
          isMarketing
            ? WAPI_EVENTS.MESSAGE_TEMPLATE_MARKETING_SENT
            : WAPI_EVENTS.MESSAGE_TEMPLATE_UTILITY_SENT,
          {
            templateId,
            templateName,
            recipientNumber: selectedLeadForChatModal.handle,
            conversationId: selectedChat?.id ?? '',
            isFreeMessage: false,
          },
        );
      }

      setTemplateCounter((prev: tSentTemplateCounter | undefined) => ({
        hasSentTemplate: prev?.hasSentTemplate ?? false,
        conversationExpiresAt: prev?.conversationExpiresAt ?? '',
        marketingWhileClosed: isMarketing
          ? (prev?.marketingWhileClosed || 0) + 1
          : (prev?.marketingWhileClosed ?? 0),
        utilityWhileClosed: isMarketing
          ? (prev?.utilityWhileClosed ?? 0)
          : (prev?.utilityWhileClosed || 0) + 1,
      }));

      // Update sentTemplateCounter in selectedChat
      if (selectedChat?.sentTemplateCounter) {
        setSelectedChat((prev) => {
          if (!prev || !prev.sentTemplateCounter) return prev;
          return {
            ...prev,
            sentTemplateCounter: {
              ...prev.sentTemplateCounter,
              marketingWhileClosed: isMarketing
                ? (prev.sentTemplateCounter.marketingWhileClosed || 0) + 1
                : prev.sentTemplateCounter.marketingWhileClosed,
              utilityWhileClosed: isMarketing
                ? prev.sentTemplateCounter.utilityWhileClosed
                : (prev.sentTemplateCounter.utilityWhileClosed || 0) + 1,
            },
          };
        });
      }
    } catch (error) {
      const isMarketingErr =
        wapiVariables?.template_type === 'MARKETING' ||
        wapiVariables?.template_type === 'FOLLOWING';

      // Extract upstream domain code (e.g., "wba-10025") when available
      const axiosErr = isAxiosError(error) ? error : undefined;
      const serverCode =
        axiosErr?.response?.data?.code ?? axiosErr?.response?.data?.error;

      trackWapiEvent(
        isMarketingErr
          ? WAPI_EVENTS.MESSAGE_TEMPLATE_MARKETING_FAILED
          : WAPI_EVENTS.MESSAGE_TEMPLATE_UTILITY_FAILED,
        {
          templateId: wapiVariables?.template_id ?? '',
          templateName: wapiVariables?.title ?? '',
          recipientNumber: selectedLeadForChatModal?.handle ?? '',
          errorMessage:
            serverCode ??
            (error instanceof Error ? error.message : String(error)),
        },
      );

      // Look up the upstream code in the error directory for a friendly
      // localized message (e.g., wba-10025 → "No tienes créditos
      // suficientes para enviar esta plantilla."). Never falls back to the
      // backend's raw `message` — see resolveSendErrorAlert.
      addAlert({
        type: eAlertType.ERROR,
        message: resolveSendErrorAlert(error, t, canTopUpBalance, 'any')
          .message,
      });
    } finally {
      setSelectedAction(ChatEnum.CHAT);
      isSendingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (headerNeedsMedia) {
      // Empty means nothing safe to prefill — no hosted copy, or a format the
      // send gate rejects. The component renders guidance for that state
      // instead of a silently disabled send button (BUG-567). The producer
      // always supplies an array, so an empty one is the "no media" signal;
      // testing truthiness here would never see it (BUG-817).
      setImageUrl(wapiVariables?.url_example?.[0] || '');
      setImageFileName(wapiVariables?.filename ?? '');
    }
  }, [headerNeedsMedia, wapiVariables?.header, wapiVariables?.filename]);

  // A new media URL gets a fresh chance to render its preview.
  useEffect(() => {
    setPreviewFailed(false);
  }, [imageUrl, wapiVariables?.header]);

  useEffect(() => {
    setCouponValues({});
  }, [wapiVariables?.template_id]);

  // Named predicates shared by the disabled gate and the explanatory UI so
  // the send button can never be disabled without a visible reason: empty →
  // guidance text, invalid format → inline error (BUG-567).
  const isMediaUrlEmpty = imageUrl.trim() === '';
  const isUrlFormatInvalid = !isMediaUrlEmpty && !isValidUrlFormat(imageUrl);

  const isDisabled = useMemo(() => {
    if (headerNeedsMedia) {
      if (isMediaUrlEmpty || isUrlFormatInvalid) {
        return true;
      }
    }
    if (wapiVariables?.parameters?.length) {
      for (const param of wapiVariables.parameters) {
        if (!paramValues[param.name] || paramValues[param.name].trim() === '') {
          return true;
        }
      }
    }
    if (wapiVariables?.copyCodeButtons?.length) {
      for (const btn of wapiVariables.copyCodeButtons) {
        if (!couponValues[btn.index] || couponValues[btn.index].trim() === '') {
          return true;
        }
      }
    }
    return false;
  }, [
    paramValues,
    couponValues,
    headerNeedsMedia,
    isMediaUrlEmpty,
    isUrlFormatInvalid,
    wapiVariables,
  ]);

  const hasMissingPredefinedValues = useMemo(() => {
    if (predefinedKeys.size === 0) return false;
    for (const key of predefinedKeys) {
      if (!paramValues[key] || paramValues[key].trim() === '') {
        return true;
      }
    }
    return false;
  }, [predefinedKeys, paramValues]);

  return {
    loading,
    paramValues,
    couponValues,
    predefinedKeys,
    hasMissingPredefinedValues,
    imageUrl,
    loadingImage,
    blockImgText,
    imageRef,
    isDisabled,
    expectedMediaKind,
    mediaLimits,
    // BUG-866: el componente pinta (o no) TODO el bloque de adjuntar con
    // esto — label, input de URL, <input type=file>, botón, guía y error.
    headerNeedsMedia,
    isMediaUrlEmpty,
    isUrlFormatInvalid,
    // RET-1806, DS-03 §5: estado del aviso de saldo insuficiente.
    isBlocked,
    costLabel,
    shortfall,
    handleChange,
    handleCouponChange,
    handleUrlChange,
    uploadImage,
    renderTemplate,
    renderHeader,
    handleSendTemplate,
    isInactiveModalOpen,
    closeInactiveModal: () => setIsInactiveModalOpen(false),
    isBlockedLeadModalOpen,
    closeBlockedLeadModal: () => setIsBlockedLeadModalOpen(false),
    confirmInactiveModal: async () => {
      if (!pendingRequest || !currentWorkspaceId || isSendingRef.current)
        return;
      // RET-2349: the same plan guard as `handleSendTemplate`, before balance.
      if (blockWapiPlanSend(eChannelType.WHATSAPP_API)) return;
      isSendingRef.current = true;
      setIsInactiveModalOpen(false);

      // RET-1806, DS-03: la entrada B.2 (confirmar el modal de conversación
      // inactiva) NO comparte función con handleSendTemplate en este camino
      // — el gate se llama aquí también, ANTES de setLoading(true), para que
      // un veredicto bloqueado nunca entre al try/finally de abajo.
      const canSend = await checkBalance({
        templateType: wapiVariables?.template_type ?? '',
        recipient: selectedLeadForChatModal?.handle ?? '',
        isFreeAtSource,
      });
      if (!canSend) {
        // Igual que arriba: bloquear no tranca. Aqui `setLoading` todavia no se
        // encendio, asi que solo hay que soltar el ref.
        isSendingRef.current = false;
        return;
      }

      setLoading(true);

      const isMarketing =
        wapiVariables?.template_type === 'MARKETING' ||
        wapiVariables?.template_type === 'FOLLOWING';

      try {
        // INT-1538 U10: a person sending from the chat; recorded first.
        await withHumanAction(currentWorkspaceId, selectedChat, () =>
          ChatService.sendWapiFollowTemplate(
            currentWorkspaceId,
            pendingRequest,
          ),
        );

        setTemplateCounter((prev: tSentTemplateCounter | undefined) => ({
          hasSentTemplate: prev?.hasSentTemplate ?? false,
          conversationExpiresAt: prev?.conversationExpiresAt ?? '',
          marketingWhileClosed: isMarketing
            ? (prev?.marketingWhileClosed || 0) + 1
            : (prev?.marketingWhileClosed ?? 0),
          utilityWhileClosed: isMarketing
            ? (prev?.utilityWhileClosed ?? 0)
            : (prev?.utilityWhileClosed || 0) + 1,
        }));

        if (selectedChat?.sentTemplateCounter) {
          setSelectedChat((prev) => {
            if (!prev || !prev.sentTemplateCounter) return prev;
            return {
              ...prev,
              sentTemplateCounter: {
                ...prev.sentTemplateCounter,
                marketingWhileClosed: isMarketing
                  ? (prev.sentTemplateCounter.marketingWhileClosed || 0) + 1
                  : prev.sentTemplateCounter.marketingWhileClosed,
                utilityWhileClosed: isMarketing
                  ? prev.sentTemplateCounter.utilityWhileClosed
                  : (prev.sentTemplateCounter.utilityWhileClosed || 0) + 1,
              },
            };
          });
        }
      } catch (error) {
        // RET-2103: same lookup, translation, and generic fallback as
        // handleSendTemplate's catch — this used to interpolate the caught
        // error directly (`${error}`), which painted the axios stringified
        // error ("Error: Request failed with status code 402") for the
        // customer.
        addAlert({
          type: eAlertType.ERROR,
          message: resolveSendErrorAlert(error, t, canTopUpBalance, 'any')
            .message,
        });
      } finally {
        setPendingRequest(null);
        setSelectedAction(ChatEnum.CHAT);
        isSendingRef.current = false;
        setLoading(false);
      }
    },
  };
}
